import { lazy, Suspense } from 'react'
import { Route, Routes } from 'react-router-dom'
import { RequireAuth } from './components/RequireAuth'
import { RequireApproved } from './components/RequireApproved'
import { RequirePartner } from './components/RequirePartner'
import { RequireOrganization } from './components/RequireOrganization'
import { RequireNotOrganization } from './components/RequireNotOrganization'
import { AppShellLayout } from './components/AppShellLayout'
import { OrgAppShellLayout } from './components/OrgAppShellLayout'
import { RootRedirect } from './components/RootRedirect'
import { RouteLoader } from './components/RouteLoader'

const SignIn = lazy(() => import('./pages/SignIn'))
const Verification = lazy(() => import('./pages/Verification'))
const Onboarding = lazy(() => import('./pages/Onboarding'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const AudioList = lazy(() => import('./pages/AudioList'))
const AudioNew = lazy(() => import('./pages/AudioNew'))
const AudioEdit = lazy(() => import('./pages/AudioEdit'))
const Profile = lazy(() => import('./pages/Profile'))
const Stats = lazy(() => import('./pages/Stats'))
const Income = lazy(() => import('./pages/Income'))
const Data = lazy(() => import('./pages/Data'))
const Settings = lazy(() => import('./pages/Settings'))
const OrgAudioList = lazy(() => import('./pages/OrgAudioList'))
const OrgAudioNew = lazy(() => import('./pages/OrgAudioNew'))
const OrgAudioEdit = lazy(() => import('./pages/OrgAudioEdit'))
const OrgDeliveryReport = lazy(() => import('./pages/OrgDeliveryReport'))
const OrgAudioAiNew = lazy(() => import('./pages/OrgAudioAiNew'))
const OrgAudioAiReview = lazy(() => import('./pages/OrgAudioAiReview'))
const OrgAiTemplate = lazy(() => import('./pages/OrgAiTemplate'))
const OrgEmailPodcast = lazy(() => import('./pages/OrgEmailPodcast'))
const OrgDailyDigest = lazy(() => import('./pages/OrgDailyDigest'))
const OrgMembers = lazy(() => import('./pages/OrgMembers'))
const OrgEmailDomains = lazy(() => import('./pages/OrgEmailDomains'))
const OrgInviteCodes = lazy(() => import('./pages/OrgInviteCodes'))
const OrgAudienceGroups = lazy(() => import('./pages/OrgAudienceGroups'))
const OrgFeedSources = lazy(() => import('./pages/OrgFeedSources'))
const OrgKeywords = lazy(() => import('./pages/OrgKeywords'))
const OrgProfile = lazy(() => import('./pages/OrgProfile'))

export default function App() {
  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route element={<RequireAuth />}>
          {/* Org-admin portal (separate Organization identity). */}
          <Route element={<RequireOrganization />}>
            <Route element={<OrgAppShellLayout />}>
              <Route path="/org/audio" element={<OrgAudioList />} />
              <Route path="/org/audio/new" element={<OrgAudioNew />} />
              <Route path="/org/audio/ai" element={<OrgAudioAiNew />} />
              <Route path="/org/audio/ai/:id" element={<OrgAudioAiReview />} />
              <Route path="/org/audio/:id/delivery-report" element={<OrgDeliveryReport />} />
              <Route path="/org/audio/:id" element={<OrgAudioEdit />} />
              <Route path="/org/ai-template" element={<OrgAiTemplate />} />
              <Route path="/org/email-podcast" element={<OrgEmailPodcast />} />
              <Route path="/org/daily-digest" element={<OrgDailyDigest />} />
              <Route path="/org/members" element={<OrgMembers />} />
              <Route path="/org/domains" element={<OrgEmailDomains />} />
              <Route path="/org/invite-codes" element={<OrgInviteCodes />} />
              <Route path="/org/audience-groups" element={<OrgAudienceGroups />} />
              <Route path="/org/feed-sources" element={<OrgFeedSources />} />
              <Route path="/org/keywords" element={<OrgKeywords />} />
              <Route path="/org/profile" element={<OrgProfile />} />
            </Route>
          </Route>

          {/* Creator portal. Org admins are bounced to their own portal before
              the access call (and its lazy PartnerAccess row) can fire. */}
          <Route element={<RequireNotOrganization />}>
            <Route path="/verification" element={<Verification />} />
            <Route element={<RequireApproved />}>
              <Route path="/onboarding" element={<Onboarding />} />
              <Route element={<RequirePartner />}>
                <Route element={<AppShellLayout />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/stats" element={<Stats />} />
                  <Route path="/audio" element={<AudioList />} />
                  <Route path="/audio/new" element={<AudioNew />} />
                  <Route path="/audio/:id" element={<AudioEdit />} />
                  <Route path="/income" element={<Income />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/data" element={<Data />} />
                  <Route path="/settings" element={<Settings />} />
                </Route>
              </Route>
            </Route>
          </Route>

          {/* Authenticated landing — routes to the org or creator portal. */}
          <Route path="*" element={<RootRedirect />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
